import { useMemo, useRef, useState } from "react";
import { Calendar, ImagePlus, Loader2, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { compressImages } from "../lib/imageCompress";
import { runResumableUpload, UploadCancelled } from "../lib/uploadResume";
import { UploadPausedBanner } from "./UploadPausedBanner";
import { photoDate, photoGps, localYMD } from "../lib/photoDates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const fmtDay = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-BE", { weekday: "short", day: "numeric", month: "short" }) : "");

export const SmartPhotoImport = ({ trip, onRefresh }) => {
  const inputRef = useRef(null);
  const [items, setItems] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [unknownDay, setUnknownDay] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [paused, setPaused] = useState(null);
  const days = trip.days || [];
  const byDate = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d.day_number])), [days]);

  const analyze = async (files) => {
    if (!files.length) return;
    setAnalyzing(true);
    const out = [];
    for (const f of files.slice(0, 60)) {
      const d = await photoDate(f);
      const g = await photoGps(f);
      const ymd = d ? localYMD(d) : null;
      out.push({ file: f, date: d, gps: g, ymd, dayNumber: ymd ? byDate[ymd] ?? null : null, url: URL.createObjectURL(f) });
    }
    setItems(out);
    setUnknownDay("");
    setAnalyzing(false);
  };

  const close = () => {
    (items || []).forEach((it) => URL.revokeObjectURL(it.url));
    setItems(null);
    setProgress("");
  };

  const grouped = useMemo(() => {
    if (!items) return { known: [], unknown: [] };
    const map = new Map();
    const unknown = [];
    items.forEach((it) => {
      if (it.dayNumber != null) {
        if (!map.has(it.dayNumber)) map.set(it.dayNumber, []);
        map.get(it.dayNumber).push(it);
      } else {
        unknown.push(it);
      }
    });
    const known = [...map.entries()].sort((a, b) => a[0] - b[0]).map(([n, list]) => ({ dayNumber: n, date: days.find((d) => d.day_number === n)?.date, list }));
    return { known, unknown };
  }, [items, days]);

  const importCount = grouped.known.reduce((s, g) => s + g.list.length, 0) + (unknownDay ? grouped.unknown.length : 0);

  const startImport = async () => {
    setUploading(true);
    const us = { done: 0, total: 0 };
    try {
      const jobs = grouped.known.map((g) => ({ dayNumber: g.dayNumber, date: g.date, list: g.list, forceDayDate: false }));
      if (unknownDay && grouped.unknown.length) {
        const d = days.find((x) => x.day_number === Number(unknownDay));
        jobs.push({ dayNumber: Number(unknownDay), date: d?.date, list: grouped.unknown, forceDayDate: true });
      }
      const total = jobs.reduce((s, j) => s + j.list.length, 0);
      us.total = total;
      let daysTouched = 0;
      const distKm = (a, b) => {
        const R = 6371, dLa = ((b.lat - a.lat) * Math.PI) / 180, dLo = ((b.lon - a.lon) * Math.PI) / 180;
        const h = Math.sin(dLa / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLo / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(h));
      };
      const steps = [];
      for (const job of jobs) {
        daysTouched += 1;
        const sorted = [...job.list].sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
        const clusters = [];
        for (const it of sorted) {
          const c = it.gps
            ? clusters.find((cl) => cl.gps && distKm(cl.gps, it.gps) <= 2)
            : clusters.find((cl) => !cl.gps);
          if (c) c.items.push(it);
          else clusters.push({ gps: it.gps || null, items: [it] });
        }
        for (const cl of clusters) {
          for (let i = 0; i < cl.items.length; i += 10) {
            const chunk = cl.items.slice(i, i + 10);
            steps.push(async () => {
              const compressed = await compressImages(chunk.map((c) => c.file));
              const fd = new FormData();
              compressed.forEach((f) => fd.append("files", f));
              fd.append("day_number", job.dayNumber);
              const ts = job.forceDayDate ? null : chunk.find((c) => c.date)?.date;
              fd.append("timestamp", ts ? ts.toISOString() : `${job.date}T12:00:00`);
              if (cl.gps) { fd.append("lat", cl.gps.lat); fd.append("lon", cl.gps.lon); }
              await api.post(`/trips/${trip.id}/media`, fd);
              us.done += chunk.length;
              setProgress(`${us.done} van ${total} geüpload...`);
            });
          }
        }
      }
      await runResumableUpload(steps, {
        onPaused: (p) => setPaused({ ...p, done: us.done, total: us.total }),
        onResumed: () => setPaused(null),
      });
      toast.success(`${us.done} foto's/video's geïmporteerd, verdeeld over ${daysTouched} dag${daysTouched === 1 ? "" : "en"}!`);
      close();
      onRefresh?.();
    } catch (err) {
      if (err instanceof UploadCancelled) {
        if (us.done > 0) {
          toast.info(`Import gestopt — ${us.done} van ${us.total} bestanden staan al in je reislog`);
          close();
          onRefresh?.();
        } else {
          toast.info("Import gestopt — er is nog niets geïmporteerd");
          setProgress("");
        }
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
        setProgress("");
      }
    } finally {
      setUploading(false);
      setPaused(null);
    }
  };

  return (
    <>
      <button data-testid="smart-import-button" type="button" onClick={() => inputRef.current?.click()} disabled={analyzing}
        className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full bg-[#B5838D] hover:bg-[#8E5D67] text-white px-5 py-2.5 text-sm font-bold active:scale-95 transition-[transform,background-color] duration-200 mb-4">
        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        Foto's slim importeren
      </button>
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="smart-import-input"
        onChange={(e) => { const arr = [...(e.target.files || [])]; e.target.value = ""; analyze(arr); }} />

      <Dialog open={!!items} onOpenChange={(o) => { if (!o && !uploading) close(); }}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="smart-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#B5838D]" /> Slim importeren</DialogTitle>
            <DialogDescription className="text-xs">
              We lazen de opnamedatum van je foto's en verdelen ze automatisch over de juiste dagen van je reislog.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {grouped.known.map((g) => (
              <div key={g.dayNumber} data-testid={`smart-import-day-${g.dayNumber}`}>
                <p className="text-sm font-semibold flex items-center gap-1.5 mb-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#B5838D]" /> Dag {g.dayNumber} · {fmtDay(g.date)}
                  <span className="text-xs text-muted-foreground font-normal">— {g.list.length} foto{g.list.length === 1 ? "" : "'s"}</span>
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {g.list.slice(0, 8).map((it, i) => (
                    it.file.type.startsWith("video/")
                      ? <video key={i} src={it.url} muted className="w-14 h-14 rounded-lg object-cover bg-black" />
                      : <img key={i} src={it.url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  ))}
                  {g.list.length > 8 && <span className="w-14 h-14 rounded-lg bg-secondary flex items-center justify-center text-xs font-bold">+{g.list.length - 8}</span>}
                </div>
              </div>
            ))}

            {grouped.unknown.length > 0 && (
              <div className="bg-secondary/60 rounded-xl p-3" data-testid="smart-import-unknown">
                <p className="text-sm font-semibold mb-1.5">Onbekende of afwijkende datum — {grouped.unknown.length} bestand{grouped.unknown.length === 1 ? "" : "en"}</p>
                <p className="text-xs text-muted-foreground mb-2">Deze hebben geen opnamedatum, of een datum buiten je reis{grouped.unknown.some((u) => u.ymd) ? ` (o.a. ${[...new Set(grouped.unknown.filter((u) => u.ymd).map((u) => fmtDay(u.ymd)))].slice(0, 3).join(", ")})` : ""}. Bij welke dag horen ze?</p>
                <div className="flex gap-1.5 flex-wrap mb-2">
                  {grouped.unknown.slice(0, 8).map((it, i) => (
                    it.file.type.startsWith("video/")
                      ? <video key={i} src={it.url} muted className="w-14 h-14 rounded-lg object-cover bg-black" />
                      : <img key={i} src={it.url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  ))}
                  {grouped.unknown.length > 8 && <span className="w-14 h-14 rounded-lg bg-card flex items-center justify-center text-xs font-bold">+{grouped.unknown.length - 8}</span>}
                </div>
                <select data-testid="smart-import-unknown-day" value={unknownDay} onChange={(e) => setUnknownDay(e.target.value)}
                  className="w-full bg-card rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#B5838D]">
                  <option value="">— Overslaan (niet importeren) —</option>
                  {days.map((d) => <option key={d.day_number} value={d.day_number}>{`Dag ${d.day_number} · ${fmtDay(d.date)}`}</option>)}
                </select>
              </div>
            )}

            {grouped.known.length === 0 && grouped.unknown.length === 0 && (
              <p className="text-sm text-muted-foreground">Geen bestanden gevonden.</p>
            )}

            <UploadPausedBanner paused={paused} testId="smart-import-paused" />

            <div className="flex items-center gap-3 pt-1">
              <button data-testid="smart-import-start" type="button" onClick={startImport} disabled={uploading || importCount === 0}
                className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-[#1B4332] px-5 py-2.5 text-sm font-bold disabled:opacity-40 active:scale-95 transition-[transform,background-color] duration-200">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                Importeer {importCount} bestand{importCount === 1 ? "" : "en"}
              </button>
              {!uploading && (
                <button type="button" onClick={close} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" /> Annuleer
                </button>
              )}
              {progress && <span className="text-xs font-mono text-muted-foreground" data-testid="smart-import-progress">{progress}</span>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
